import { twMerge } from "tailwind-merge";

export type ButtonKind =
  | "primary"
  | "secondary"
  | "accent"
  | "plain"
  | "link"
  | "danger"
  | "action"
  | "dropdown"
  | "select"
  | "topNav";

export interface ButtonClassesOptions {
  kind: ButtonKind;
  className?: string;
  groupPosition?: "left" | "middle" | "right";
  round?: boolean;
  compact?: boolean;
  /** Toggled / selected look (e.g. aria-pressed). Composes with any `kind` — not a separate kind. */
  pressed?: boolean;
}

function colorToClasses(
  color: "brand" | "green" | "red" | "secondary" | "action" | "dropdown" | "select" | "topNav",
  groupPosition?: "left" | "middle" | "right",
  round?: boolean,
): string[] {
  let colors: string[] = [];

  switch (color) {
    case "brand":
      colors = [
        "bg-brand-700",
        "bordershadow-brand-fixed-800",
        "hover:bordershadow-brand-fixed-1000",
        "hover:bg-brand-600",
        "focus-visible:outline-brand-600",
        "text-white",
        "hover:text-white",
        "active:border active:border-brand-400",
      ];

      if (groupPosition) {
        colors.push(
          "border",
          "border-t-brand-600",
          "border-b-brand-700",
          "hover:border-t-brand-700",
          "hover:border-b-brand-800",
        );
        if (groupPosition === "left") {
          colors.push("border-l-brand-600", "hover:border-l-brand-700");
        }
      } else {
        colors.push(
          "border",
          "border-t-brand-600",
          "border-r-brand-700",
          "border-b-brand-700",
          "border-l-brand-600",
          "hover:border-t-brand-700",
          "hover:border-r-brand-800",
          "hover:border-b-brand-800",
          "hover:border-l-brand-700",
        );
      }
      return colors;
    case "red":
      colors = [
        "bg-red-700",
        "bordershadow-red-fixed-800",
        "hover:bordershadow-red-fixed-1000",
        "hover:bg-red-600",
        "focus-visible:outline-red-600",
        "text-white",
      ];

      if (!groupPosition) {
        colors.push(
          "border",
          "border-t-red-600",
          "border-r-red-700",
          "border-b-red-700",
          "border-l-red-600",
          "hover:border-t-red-700",
          "hover:border-r-red-800",
          "hover:border-b-red-800",
          "hover:border-l-red-700",
        );
      }
      return colors;
    case "green":
      if (round) {
        colors = [
          "text-white stroke-white fill-white border-green-500 bg-green-700 shadow-green-800/40 hover:border-green-600 hover:bg-green-600",
        ];
      } else {
        colors = [
          "bg-green-600",
          "bordershadow-green-fixed-700",
          "hover:bordershadow-green-fixed-900",
          "hover:bg-green-600",
          "hover:shadow-lg",
          "focus-visible:outline-green-500",
          "text-white",
          "hover:text-white",
        ];
      }

      if (!groupPosition) {
        colors.push(
          "border",
          "border-t-green-500",
          "border-r-green-600",
          "border-b-green-600",
          "border-l-green-500",
          "hover:border-t-green-600",
          "hover:border-r-green-700",
          "hover:border-b-green-700",
          "hover:border-l-green-600",
        );
      }
      return colors;
    case "secondary":
      return [
        "bg-gray-50 dark:bg-gray-800",
        "text-gray-800 dark:text-gray-200",
        "border border-gray-300 dark:border-gray-700",
        "hover:bg-gray-100 dark:hover:bg-gray-700",
        "shadow-sm",
        "focus-visible:outline-gray-400",
        "active:bg-gray-200",
        "checked:bg-gray-200 aria-checked:bg-gray-200",
        "dark:checked:bg-gray-600 dark:aria-checked:bg-gray-600",
      ];
    case "action":
      return [
        "bg-gray-100",
        "bordershadow-gray-fixed-400",
        "hover:bordershadow-gray-fixed-700",
        "hover:bg-gray-50",
        "focus-visible:outline-gray-300",
        "text-gray-600",
        "shadow-sm shadow-gray-300",
        "px-2",
        "py-1",
        "border border-gray-400",
      ];
    case "dropdown":
      return [
        "rounded-lg border border-gray-300 hover:border-gray-400 py-2 pl-3 pr-10 text-left shadow-md ui-open:border-gray-100",
      ];
    case "select":
      return [
        "pl-2 pr-7 block w-full bg-white border border-gray-300 stroke-gray-500 rounded text-left min-h-[42px]",
      ];
    case "topNav":
      return [
        "bg-slate-800",
        "text-white",
        "hover:bg-slate-700",
        "border-0",
        "cursor-pointer",
        "flex",
        "items-center",
        "justify-center",
        "px-3",
      ];
  }
}

/** Extra classes when `pressed` is true; works with any button kind. */
function pressedToggleClasses(kind: ButtonKind): string {
  switch (kind) {
    case "secondary":
    case "action":
      return [
        "shadow-inner",
        "bg-gray-100 dark:bg-gray-700",
        "ring-2 ring-inset ring-gray-400/55 dark:ring-gray-500/60",
      ].join(" ");
    case "dropdown":
    case "select":
      return ["shadow-inner", "ring-2 ring-inset ring-gray-400/50"].join(" ");
    case "link":
      return ["rounded-sm", "bg-brand-50 dark:bg-brand-950/40", "ring-2 ring-inset ring-brand-400/45"].join(
        " ",
      );
    case "plain":
      return "rounded-sm ring-2 ring-inset ring-gray-400/45";
    case "danger":
    case "accent":
    case "topNav":
    default:
      return [
        "shadow-[inset_0_3px_14px_rgba(0,0,0,0.42)]",
        "ring-2 ring-inset ring-white/35",
        "translate-y-px",
      ].join(" ");
  }
}

export function buttonClasses(options: ButtonClassesOptions) {
  const { kind, className, groupPosition, round, compact, pressed } = options;

  if (kind === "plain") {
    return twMerge(pressed && pressedToggleClasses("plain"), className);
  }

  if (kind === "link") {
    return twMerge(
      "text-brand-700",
      "hover:text-brand-800",
      "focus-visible:outline-brand-500",
      "active:text-brand-900",
      "hover:underline",
      "cursor-pointer",
      pressed && pressedToggleClasses("link"),
      className,
    );
  }

  const colorClasses = [
    "fill-white",
    // "stroke-white",
    "disabled:bg-gray-300",
    "disabled:text-gray-500",
    "disabled:fill-gray-500",
    "disabled:stroke-gray-500",
  ];

  // if (!groupPosition) {
  colorClasses.push(
    "disabled:border-t-gray-400",
    "disabled:border-r-gray-400",
    "disabled:border-b-gray-400",
    "disabled:border-l-gray-400",
  );
  // }

  switch (kind) {
    case "danger": {
      colorClasses.push(...colorToClasses("red", groupPosition, false));
      break;
    }
    case "accent": {
      colorClasses.push(...colorToClasses("green", groupPosition, round));
      break;
    }
    case "secondary":
    case "action":
    case "dropdown":
    case "select":
    case "topNav": {
      colorClasses.push(...colorToClasses(kind, groupPosition, false));
      break;
    }
    default: {
      colorClasses.push(...colorToClasses("brand", groupPosition, false));
      break;
    }
  }

  if (round) {
    colorClasses.push("h-[55px] w-[55px] rounded-full font-semibold tracking-wide shadow-md");
  }

  const classes = [
    "relative",
    "inline-flex",
    "items-center",
    "space-x-2",
    "leading-4",
    "text-center",
    "transition-all",
    "duration-200",
    "ease-out",
    "rounded",
    "shadow-sm",
    "outline-none",
    "cursor-pointer",
    "font-normal",
    "outline-0",
    "focus-visible:outline-4",
    "focus-visible:outline-offset-1",
    "disabled:cursor-not-allowed",
    "text-sm",
  ];

  if (kind === "topNav") {
    // TopNav buttons use header height CSS variable, no default padding
  } else if (compact) {
    classes.push("px-2", "py-1");
  } else {
    classes.push("px-3", "py-2");
  }

  return twMerge(classes, colorClasses, pressed && pressedToggleClasses(kind), className);
}
